import { useEffect, useRef } from "react";
import * as THREE from "three";
import { Sprout, Wheat, Building2, HeartPulse } from "lucide-react";

const SECTORS = [
    { key: "seeds", name: "Agriculture & Hybrid Seeds", color: "#58A618", hex: 0x58a618, Icon: Sprout },
    { key: "fmcg", name: "FMCG & Foods", color: "#F16622", hex: 0xf16622, Icon: Wheat },
    { key: "realty", name: "Real Estate", color: "#60A5FA", hex: 0x60a5fa, Icon: Building2 },
    { key: "health", name: "Pharma & Healthcare", color: "#2EC4B6", hex: 0x2ec4b6, Icon: HeartPulse },
];

const RING_R = 2.45;

export const SectorMark = ({ className = "" }) => {
    const mountRef = useRef(null);
    const labelRefs = useRef([]);

    useEffect(() => {
        const mount = mountRef.current;
        if (!mount) return;

        const scene = new THREE.Scene();
        const camera = new THREE.PerspectiveCamera(42, mount.clientWidth / mount.clientHeight, 0.1, 100);
        camera.position.set(0, 0.5, 7.8);
        const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
        renderer.setSize(mount.clientWidth, mount.clientHeight);
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        mount.appendChild(renderer.domElement);

        const root = new THREE.Group();
        scene.add(root);

        /* ---- Central emblem: the real MS pinwheel mark, floating in 3D ---- */
        const emblem = new THREE.Group();
        const glow = new THREE.Mesh(
            new THREE.CircleGeometry(1.42, 48),
            new THREE.MeshBasicMaterial({ color: 0xf16622, transparent: true, opacity: 0.07 })
        );
        glow.position.z = -0.12;
        emblem.add(glow);
        const haloRing = new THREE.Mesh(
            new THREE.TorusGeometry(1.34, 0.008, 8, 160),
            new THREE.MeshBasicMaterial({ color: 0xf16622, transparent: true, opacity: 0.35 })
        );
        emblem.add(haloRing);
        const mark = new THREE.Mesh(
            new THREE.PlaneGeometry(2.1, 2.1),
            new THREE.MeshBasicMaterial({ transparent: true, side: THREE.DoubleSide })
        );
        new THREE.TextureLoader().load("/assets/favicon-icon.png", (tex) => {
            tex.colorSpace = THREE.SRGBColorSpace;
            mark.material.map = tex;
            mark.material.needsUpdate = true;
        });
        emblem.add(mark);
        root.add(emblem);

        /* ---- Orbit ring + sector nodes (shared tilted plane) ---- */
        const orbitGroup = new THREE.Group();
        orbitGroup.rotation.x = 0.42;
        root.add(orbitGroup);

        const ring = new THREE.Mesh(
            new THREE.TorusGeometry(RING_R, 0.006, 8, 240),
            new THREE.MeshBasicMaterial({ color: 0x232b6b, transparent: true, opacity: 0.22 })
        );
        orbitGroup.add(ring);

        const nodes = [];
        const lines = [];
        SECTORS.forEach((s, i) => {
            const node = new THREE.Mesh(
                new THREE.SphereGeometry(0.14, 28, 28),
                new THREE.MeshBasicMaterial({ color: s.hex })
            );
            const halo = new THREE.Mesh(
                new THREE.SphereGeometry(0.27, 24, 24),
                new THREE.MeshBasicMaterial({ color: s.hex, transparent: true, opacity: 0.2 })
            );
            node.add(halo);
            orbitGroup.add(node);
            nodes.push({ node, phase: (i * Math.PI) / 2 });

            const geo = new THREE.BufferGeometry();
            geo.setAttribute("position", new THREE.BufferAttribute(new Float32Array(6), 3));
            const line = new THREE.Line(
                geo,
                new THREE.LineBasicMaterial({ color: s.hex, transparent: true, opacity: 0.5 })
            );
            orbitGroup.add(line);
            lines.push(line);
        });

        /* ---- Ambient particles ---- */
        const PN = 240;
        const pos = new Float32Array(PN * 3);
        for (let i = 0; i < PN; i++) {
            pos[i * 3] = (Math.random() - 0.5) * 9;
            pos[i * 3 + 1] = (Math.random() - 0.5) * 6;
            pos[i * 3 + 2] = (Math.random() - 0.5) * 4 - 1;
        }
        const pGeo = new THREE.BufferGeometry();
        pGeo.setAttribute("position", new THREE.BufferAttribute(pos, 3));
        const stars = new THREE.Points(
            pGeo,
            new THREE.PointsMaterial({ color: 0x232b6b, size: 0.022, transparent: true, opacity: 0.22 })
        );
        scene.add(stars);

        let mx = 0;
        let my = 0;
        const onMouse = (e) => {
            const rect = mount.getBoundingClientRect();
            mx = ((e.clientX - rect.left) / rect.width - 0.5) * 2;
            my = ((e.clientY - rect.top) / rect.height - 0.5) * 2;
        };
        mount.addEventListener("mousemove", onMouse);

        const v = new THREE.Vector3();
        let raf;
        const clock = new THREE.Clock();
        const animate = () => {
            const t = clock.getElapsedTime();

            emblem.rotation.z = t * 0.22;
            emblem.rotation.x = Math.sin(t * 0.3) * 0.12;
            haloRing.rotation.z = -t * 0.1;
            root.rotation.y = Math.sin(t * 0.12) * 0.22 + mx * 0.3;
            root.rotation.x = my * 0.15;

            nodes.forEach((n, i) => {
                const a = t * 0.24 + n.phase;
                n.node.position.set(Math.cos(a) * RING_R, Math.sin(a) * RING_R, 0);
                const p = lines[i].geometry.attributes.position;
                p.setXYZ(1, n.node.position.x, n.node.position.y, n.node.position.z);
                p.needsUpdate = true;

                /* project node to screen for its label */
                const el = labelRefs.current[i];
                if (el) {
                    n.node.getWorldPosition(v);
                    const depth = v.z;
                    v.project(camera);
                    const w = mount.clientWidth;
                    const h = mount.clientHeight;
                    const x = Math.min(Math.max((v.x * 0.5 + 0.5) * w, 105), w - 105);
                    const y = Math.min(Math.max((-v.y * 0.5 + 0.5) * h, 24), h - 72);
                    const front = depth > 0.4;
                    el.style.transform = `translate(-50%,-50%) translate(${x}px, ${y}px) scale(${front ? 1 : 0.82})`;
                    el.style.opacity = front ? "1" : "0.45";
                    el.style.zIndex = front ? "5" : "1";
                }
            });

            stars.rotation.y = t * 0.012;
            renderer.render(scene, camera);
            raf = requestAnimationFrame(animate);
        };
        animate();

        const onResize = () => {
            camera.aspect = mount.clientWidth / mount.clientHeight;
            camera.updateProjectionMatrix();
            renderer.setSize(mount.clientWidth, mount.clientHeight);
        };
        window.addEventListener("resize", onResize);

        return () => {
            cancelAnimationFrame(raf);
            mount.removeEventListener("mousemove", onMouse);
            window.removeEventListener("resize", onResize);
            if (renderer.domElement.parentNode === mount) mount.removeChild(renderer.domElement);
            pGeo.dispose();
            renderer.dispose();
        };
    }, []);

    return (
        <div className={className} data-testid="sector-mark-model">
            <div ref={mountRef} className="absolute inset-0" />
            {SECTORS.map((s, i) => (
                <div
                    key={s.key}
                    ref={(el) => (labelRefs.current[i] = el)}
                    data-testid={`sector-label-${s.key}`}
                    className="pointer-events-none absolute left-0 top-0 flex items-center gap-2 whitespace-nowrap border border-[#232B6B]/10 bg-white/85 px-3 py-1.5 shadow-[0_10px_26px_-14px_rgba(10,13,32,0.35)] backdrop-blur-md transition-opacity duration-200 max-sm:hidden"
                >
                    <s.Icon className="h-3.5 w-3.5" style={{ color: s.color }} strokeWidth={2} />
                    <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#232B6B]">{s.name}</span>
                </div>
            ))}
        </div>
    );
};
